import type { DensitySnapshot, InstrumentRef, SituationView } from "@screener/protocol";
import { type DensityFilters, type DensitySort, filterRows } from "./derive";
import type { DisplayMinAgeState } from "./display-min-age";
import type { DisplayMinimumsState } from "./display-minimums";
import type { FavoritesState } from "./favorite-match";

/**
 * Ряд списка ситуаций §5. Собирается из состава ситуации и живых снимков —
 * второй истины над агрегатом не заводит: сумма, число рынков, время жизни и
 * число подходов остаются полем `situation.aggregate`, каким его прислал
 * демон (design.md, решение 2).
 */
export interface SituationRow {
  /**
   * Ключ строки списка — идентичность ряда, а не ключ ситуации §5.
   *
   * Ключом ситуации ряд не опознаётся: в режиме `by_pair` биржи в ключе нет
   * вовсе, и все стены одной монеты и стороны — сколько бы их ни стояло на
   * разных ценах — несут один и тот же `${base}|${quote}|${side}|`. Ключ
   * ситуации ровно поэтому и не уникален: §5 задаёт зону как окрестность
   * якоря, то есть отношение близости, а не значение поля. React на
   * одинаковых ключах схлопывал строки, а раскрытие по такому ключу
   * раскрывало разом все одноимённые группы (найдено первым боевым
   * подъёмом, 22.09.2026).
   *
   * Поэтому идентичность берётся от состава: `density_id` участника с
   * наименьшей ценой якоря — первого в составе по контракту (`SituationView.
   * members`, «в порядке возрастания цены якоря»). Это не якорь плотности §4.3
   * и не устойчивая величина: пришедший ниже участник меняет ключ, а с ним —
   * и место строки в React, то есть раскрытые участники перерисовываются
   * заново, а точка табуляции откатывается к началу списка. Устойчивого
   * идентификатора ситуации контракт не даёт вовсе, и обмен сделан сознательно:
   * неуникальный ключ схлопывал строки на каждом такте, этот — перерисовывает
   * одну группу тогда, когда её состав изменился снизу (ревью фронтовой части,
   * 22.09.2026, пункт 2; вопрос записан в `knowledge/open-questions.md`).
   *
   * Ситуации не пересекаются составами — ядро сцепляет упорядоченные плотности
   * в непересекающиеся цепочки, — и такой ключ уникален среди рядов одного
   * такта.
   */
  key: string;
  /**
   * Все участники по контракту, включая тех, чьих снимков у клиента нет:
   * раскрытие держится на составе, а не на ключе (`memberIds`), и отбор
   * на эту принадлежность влиять не вправе — скрытый фильтром участник из
   * группы не уходит.
   */
  memberIds: string[];
  situation: SituationView;
  /** Видимые участники — после отбора, в порядке состава контракта. */
  members: DensitySnapshot[];
  /** Участники, чей снимок есть у клиента, — видимые и скрытые отбором вместе. */
  knownCount: number;
  /** Известные участники, скрытые отбором. */
  hiddenCount: number;
  /**
   * Наименьший якорь видимых участников, строкой как в снимке. `null`, когда
   * ни у одного видимого участника нет конечного числового якоря — диапазона
   * тогда попросту нет, и подставлять «0» вместо него нельзя: это выглядело
   * бы как настоящая цена там, где верной цены не существует.
   */
  anchorMin: string | null;
  /** Наибольший якорь видимых участников, строкой как в снимке; `null` — см. `anchorMin`. */
  anchorMax: string | null;
  /** Ближайший к цене видимый участник и рынок, которому принадлежит дистанция. */
  nearest: { distancePct: number; instrument: InstrumentRef } | null;
  /**
   * Ведущая плотность группы (1.51, §64 п. 1): рынок, чьи биржа и дистанция
   * стоят в строке. Это `nearest.instrument`, а когда дистанция неизвестна ни
   * у одного видимого участника — первый видимый. Видимый участник у ряда есть
   * всегда: ряд без них не собирается.
   */
  leading: InstrumentRef;
}

/**
 * Идентичность ряда: ключ ситуации §5 вместе с первым участником состава —
 * тем, чья цена якоря наименьшая.
 *
 * Ключ оставлен в строке ради читаемости логов и тестов, уникальность даёт
 * участник: ситуации не пересекаются составами, а ключ §5 уникальным не
 * является вовсе (см. `SituationRow.key`). Состав по контракту непустой,
 * но пустой список не роняет ряд: тогда в ключе остаётся одна ситуация,
 * и хуже прежнего поведения это не будет.
 */
function rowKey(situation: SituationView): string {
  const { base, quote, side, exchange } = situation.key;
  const lowestMember = situation.members[0]?.density_id ?? "";
  return `${base}|${quote}|${side}|${exchange ?? ""}|${lowestMember}`;
}

/**
 * Раскрыть состав группы или свернуть уже раскрытый.
 *
 * Пересборка по составу этого такта, а не переключение одного ключа: группа
 * раскрыта, пока в наборе хоть один её участник, и свернуть её значит убрать
 * оттуда весь нынешний состав разом.
 */
export function toggleExpandedMembers(
  previous: ReadonlySet<string>,
  memberIds: readonly string[],
): Set<string> {
  const next = new Set(previous);
  const expanded = memberIds.some((id) => next.has(id));
  for (const id of memberIds) {
    if (expanded) {
      next.delete(id);
    } else {
      next.add(id);
    }
  }
  return next;
}

/**
 * Оставляет в наборе раскрытых только участников живых ситуаций.
 *
 * Мера — состав ситуаций, а не наличие снимка: состав §7.6 вправе отставать от
 * снимков на такт, и уборка по снимкам вычёркивала бы ровно тех участников,
 * ради которых раскрытие и держится составом. Прежний набор возвращается как
 * есть, когда вычёркивать нечего, — лишняя ссылка перерисовала бы список.
 */
export function retainExpandedMembers(
  previous: Set<string>,
  situations: readonly SituationView[],
): Set<string> {
  if (previous.size === 0) {
    return previous;
  }
  const alive = new Set<string>();
  for (const situation of situations) {
    for (const member of situation.members) {
      alive.add(member.density_id);
    }
  }
  const next = new Set<string>();
  for (const id of previous) {
    if (alive.has(id)) {
      next.add(id);
    }
  }
  return next.size === previous.size ? previous : next;
}

/** Ближайший видимый участник по дистанции; `null`, если она не известна ни у одного. */
function nearestMember(members: DensitySnapshot[]): SituationRow["nearest"] {
  let nearest: SituationRow["nearest"] = null;
  for (const member of members) {
    if (member.distance_pct === null) {
      continue;
    }
    if (nearest === null || member.distance_pct < nearest.distancePct) {
      nearest = { distancePct: member.distance_pct, instrument: member.instrument };
    }
  }
  return nearest;
}

/**
 * Диапазон якорей видимых участников. Цены у ситуации в контракте нет —
 * клиент выводит её сам из якорей, которые уже знает по строкам §9
 * (design.md, решение 2). Сравнение — через `Number`, как `volumeBarFraction`
 * в `derive.ts`, но возвращаются исходные строки снимков.
 *
 * Участник с нечисловым или бесконечным якорем в сравнение не попадает: если
 * конечного якоря нет ни у одного видимого участника, диапазона попросту не
 * существует, и `null` честнее «0» — уверенного числа там, где верного нет.
 */
function anchorRange(members: DensitySnapshot[]): {
  anchorMin: string | null;
  anchorMax: string | null;
} {
  let anchorMin: string | null = null;
  let anchorMax: string | null = null;
  let minValue = Number.POSITIVE_INFINITY;
  let maxValue = Number.NEGATIVE_INFINITY;
  for (const member of members) {
    const value = Number(member.anchor_price);
    if (!Number.isFinite(value)) {
      continue;
    }
    if (value < minValue) {
      minValue = value;
      anchorMin = member.anchor_price;
    }
    if (value > maxValue) {
      maxValue = value;
      anchorMax = member.anchor_price;
    }
  }
  return { anchorMin, anchorMax };
}

/** Результат сборки рядов списка ситуаций — ряды и число отпавших без известных участников. */
export interface SituationRowsResult {
  rows: SituationRow[];
  /**
   * Ситуации, у которых не нашлось ни единого известного участника: не отбор
   * их скрыл, их участников на экране попросту ещё (или уже) нет (design.md,
   * риск отставания состава ситуаций от состава плотностей на такт). Список
   * ситуаций различает эту причину пустоты от «фильтры не пропустили».
   */
  unknownCount: number;
}

/**
 * Ряды списка ситуаций §5: состав берётся из потока §7.6, а не пересчитывается
 * группировкой на клиенте (§5 — доменная группировка ядра).
 *
 * Участник сопоставляется со строкой по `search_density_id`, а не по
 * `density_id` библиотеки. Участник, чьего снимка нет в `densities` (или у
 * которого `search_density_id` пуст), не попадает никуда: он не скрыт
 * отбором, его попросту ещё (или уже) нет. Ситуация без единого известного
 * участника в результат не попадает вовсе, а её отсутствие считается
 * отдельно в `unknownCount`.
 *
 * `favorites` — обязательный параметр, тем же доводом, что и у `filterRows`
 * в `derive.ts` (review stage6-favorites, п. 6): участник ситуации отбирается
 * теми же правилами, что и строка таблицы, и молчаливого значения по
 * умолчанию здесь быть не должно. Так же обязателен `minimums`: личные
 * минимумы §59 отбирают участников тем же `filterRows`.
 */
export function situationRows(
  situations: SituationView[],
  densities: Record<string, DensitySnapshot>,
  filters: DensityFilters,
  favorites: FavoritesState,
  minimums: DisplayMinimumsState,
  minAge: DisplayMinAgeState,
  nowMs: number,
): SituationRowsResult {
  const rows: SituationRow[] = [];
  let unknownCount = 0;

  for (const situation of situations) {
    const known: DensitySnapshot[] = [];
    for (const member of situation.members) {
      // Строки потока — плотности поиска (§60 п. 3–5): участник без имени
      // плотности поиска — участник, снимка которого у клиента нет.
      const snapshot =
        member.search_density_id === null ? undefined : densities[member.search_density_id];
      if (snapshot !== undefined) {
        known.push(snapshot);
      }
    }
    if (known.length === 0) {
      unknownCount += 1;
      continue;
    }

    // «Только избранные» отбирает участников, а не всю ситуацию (спека
    // `web-situation-list`): агрегат ниже остаётся тем, что прислал демон, по
    // всей группе.
    const visible = filterRows(known, filters, favorites, minimums, minAge, nowMs);
    const [firstVisible] = visible;
    if (firstVisible === undefined) {
      continue;
    }

    const { anchorMin, anchorMax } = anchorRange(visible);
    const nearest = nearestMember(visible);

    rows.push({
      key: rowKey(situation),
      memberIds: situation.members.map((member) => member.density_id),
      situation,
      members: visible,
      knownCount: known.length,
      hiddenCount: known.length - visible.length,
      anchorMin,
      anchorMax,
      nearest,
      leading: nearest?.instrument ?? firstVisible.instrument,
    });
  }

  return { rows, unknownCount };
}

/**
 * Сравнение по дистанции ряда ситуации. Ряд без известной дистанции уходит
 * в конец при любом направлении — то же правило, что `compareDistance`
 * в `derive.ts` для одиночной плотности: пустое значение не вправе занимать
 * место нулевой (самой тревожной) дистанции.
 */
function compareNearestDistance(left: SituationRow, right: SituationRow, sign: number): number {
  const leftKnown = left.nearest !== null;
  const rightKnown = right.nearest !== null;
  if (!leftKnown && !rightKnown) {
    return 0;
  }
  if (!leftKnown) {
    return 1;
  }
  if (!rightKnown) {
    return -1;
  }
  return sign * ((left.nearest?.distancePct ?? 0) - (right.nearest?.distancePct ?? 0));
}

/**
 * Сортировка списка ситуаций — те же три ключа §9, что и у таблицы
 * плотностей. Сумма берётся из агрегата, время жизни — от самой старой
 * плотности группы (`first_seen_min_ms`), дистанция — минимумом среди
 * видимых участников.
 */
export function sortSituationRows(rows: SituationRow[], sort: DensitySort): SituationRow[] {
  const sign = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    if (sort.key === "usd") {
      return (
        sign *
        (Number(left.situation.aggregate.total_usd) - Number(right.situation.aggregate.total_usd))
      );
    }
    if (sort.key === "age") {
      // Тот же приём, что и `sortRows` в `derive.ts`: возраст растёт вниз по
      // времени обнаружения самой старой плотности группы, поэтому по
      // возрастанию возраста порядок обратный.
      return (
        sign *
        (right.situation.aggregate.first_seen_min_ms - left.situation.aggregate.first_seen_min_ms)
      );
    }
    return compareNearestDistance(left, right, sign);
  });
}
